import { useState } from 'react';
import { Button, Badge, NumberInput } from '@atoms/index';
import { cn } from '@/utils/cn';

type RestaurantStatus = 'ONLINE' | 'OFFLINE' | 'BUSY' | 'SUSPENDED';

interface RestaurantStatusSelectorProps {
  currentStatus: RestaurantStatus;
  onStatusChange: (status: RestaurantStatus, duration?: number) => Promise<void>;
  isLoading?: boolean;
}

export const RestaurantStatusSelector = ({
  currentStatus,
  onStatusChange,
  isLoading = false,
}: RestaurantStatusSelectorProps) => {
  const [showBusyDuration, setShowBusyDuration] = useState(false);
  const [busyDuration, setBusyDuration] = useState(30);

  const statusConfig: Record<
    RestaurantStatus,
    { label: string; color: string; description: string }
  > = {
    ONLINE: {
      label: 'Açık',
      color: 'success',
      description: 'Sipariş kabul ediliyor',
    },
    OFFLINE: {
      label: 'Kapalı',
      color: 'gray',
      description: 'Sipariş kabul edilmiyor',
    },
    BUSY: {
      label: 'Meşgul',
      color: 'warning',
      description: 'Geçici olarak meşgul',
    },
    SUSPENDED: {
      label: 'Askıya Alındı',
      color: 'danger',
      description: 'Admin tarafından askıya alındı',
    },
  };

  const handleStatusClick = async (status: RestaurantStatus) => {
    if (status === 'BUSY') {
      setShowBusyDuration(true);
      return;
    }

    await onStatusChange(status);
  };

  const handleBusyConfirm = async () => {
    await onStatusChange('BUSY', busyDuration);
    setShowBusyDuration(false);
  };

  const currentConfig = statusConfig[currentStatus];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-gray-700">Durum:</span>
        <Badge variant={currentConfig.color as any} size="lg">
          {currentConfig.label}
        </Badge>
        <span className="text-sm text-gray-500">{currentConfig.description}</span>
      </div>

      {showBusyDuration ? (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
          <NumberInput
            label="Meşgul Süresi (dakika)"
            integerOnly
            value={busyDuration === 0 ? '' : String(busyDuration)}
            onChange={(s) =>
              setBusyDuration(s === '' ? 30 : Math.min(480, Math.max(1, parseInt(s, 10) || 30)))
            }
          />
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleBusyConfirm}
              disabled={isLoading}
            >
              Onayla
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowBusyDuration(false)}
              disabled={isLoading}
            >
              İptal
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {Object.entries(statusConfig).map(([status, config]) => {
            const isActive = currentStatus === status;
            const isDisabled = status === 'SUSPENDED' || isLoading;

            return (
              <Button
                key={status}
                variant={isActive ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => handleStatusClick(status as RestaurantStatus)}
                disabled={isDisabled || isActive}
                className={cn(
                  isActive && 'ring-2 ring-primary ring-offset-2',
                )}
              >
                {config.label}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
};
